import { useEffect, useState } from 'react';
import { Grid, Box, Button, TextField, Typography } from '@mui/material';
import { useAppDispatch } from '../../../redux/hooks';
import SendToMobileIcon from '@mui/icons-material/SendToMobile';
import EditIcon from '@mui/icons-material/Edit';
import { CustomerClient } from '../../../helpers/client';
import { CustomersClient } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { ErrorMessageBox } from './loginStyles';
import { authenticateAnonymous, logoutAnonymous } from '../../../redux/reducers/authReducer';
import { isValidPhoneNumber } from '../../../helpers/validator';
import { ButtonLoadingIndicator } from '../../../controls';

interface AnonymousLoginProps {
  onLogin?: () => void;
}
export function AnonymousLogin({ onLogin }: AnonymousLoginProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const customersClient: CustomersClient = new CustomerClient();
  const [mobileNumber, setMobileNumber] = useState<string>('');
  const [confirmationCode, setConfirmationCode] = useState<string>('');
  const [isSendingConfirmationCode, setIsSendingConfirmationCode] = useState<boolean>(false);
  const [codeIsSent, setCodeIsSent] = useState<boolean>(false);
  const [codeIsResent, setCodeIsResent] = useState<boolean>(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [validPhoneNumber, setValidPhoneNumber] = useState(false);

  useEffect(() => {
    setValidPhoneNumber(isValidPhoneNumber(mobileNumber));
  }, [mobileNumber]);

  const sendPhoneConfirmation = async () => {
    if (isSendingConfirmationCode || !validPhoneNumber) {
      return;
    }
    setIsSendingConfirmationCode(true);
    try {
      await customersClient.sendAnonymousPhoneConfirmation(mobileNumber);
      setCodeIsSent(true);
      setCodeIsResent(false);
    } catch {
      console.error('Not possible to send confirmation.');
    } finally {
      setIsSendingConfirmationCode(false);
    }
  };

  const resendPhoneConfirmation = async () => {
    setCodeIsResent(true);
    try {
      await customersClient.sendAnonymousPhoneConfirmation(mobileNumber);
    } catch (exception) {
      console.error(exception);
    }
  };

  const login = async () => {
    if (isAuthenticating || !confirmationCode) {
      return;
    }

    setIsAuthenticating(true);
    try {
      var verificationResult = await dispatch(authenticateAnonymous(confirmationCode)).unwrap();
      if (verificationResult?.verified) {
        setErrorMessage('');
        onLogin?.();
      } else {
        setErrorMessage(t('anonymousLogin:invalidConfirmationCode'));
        dispatch(logoutAnonymous());
      }
    } catch {
      setErrorMessage(t('anonymousLogin:invalidConfirmationCode'));
      dispatch(logoutAnonymous());
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <>
      {!codeIsSent && (
        <Box>
          <Typography variant="body2">{t('anonymousLogin:enterPhoneNumber')}</Typography>
          <Grid container spacing={2} sx={{ mt: '0px' }}>
            <Grid size={{ xs: 12, sm: 'grow' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="+370xxxxxxxx"
                autoFocus
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isSendingConfirmationCode && validPhoneNumber) {
                    e.preventDefault();
                    sendPhoneConfirmation();
                  }
                }}
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '10px',
                      minWidth: '200px',
                    },
                  },
                }}
              />
            </Grid>
            <Grid
              size={{ xs: 12, sm: 'auto' }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Button
                sx={{
                  borderRadius: '15px',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
                variant="contained"
                disabled={isSendingConfirmationCode || !validPhoneNumber}
                onClick={sendPhoneConfirmation}
                loading={isSendingConfirmationCode}
                loadingIndicator={<ButtonLoadingIndicator size={18} />}
              >
                {t('anonymousLogin:sendConfirmationCode')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      )}
      {codeIsSent && (
        <Box>
          <Typography variant="body2" sx={{ mb: '10px' }}>
            {t('anonymousLogin:enterConfirmationCode')}
            {mobileNumber}
          </Typography>
          <Grid container spacing={2} sx={{ justifyContent: 'space-between' }}>
            <Grid
              size="auto"
              sx={{
                display: 'flex',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
                gap: 0.5,
                color: 'secondary.main',
              }}
              onClick={() => setCodeIsSent(false)}
            >
              <EditIcon fontSize="small" />
              <Typography variant="body2">{t('anonymousLogin:changePhoneNumber')}</Typography>
            </Grid>
            {!codeIsResent && (
              <Grid
                size="auto"
                sx={{
                  display: 'flex',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  gap: 0.5,
                  color: 'secondary.main',
                }}
                onClick={() => resendPhoneConfirmation()}
              >
                <SendToMobileIcon fontSize="small" />
                <Typography variant="body2">{t('anonymousLogin:resendConfirmationCode')}</Typography>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: '10px', mt: '20px' }}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label={t('anonymousLogin:confirmationCode')}
                value={confirmationCode}
                onChange={(e) => setConfirmationCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !isAuthenticating && confirmationCode) {
                    e.preventDefault();
                    login();
                  }
                }}
                autoFocus
                slotProps={{
                  input: {
                    sx: {
                      borderRadius: '10px',
                    },
                  },
                }}
              />
            </Grid>
          </Grid>
          {errorMessage && <ErrorMessageBox>{errorMessage}</ErrorMessageBox>}
          <Grid container sx={{ justifyContent: 'center' }}>
            <Button
              sx={{
                width: '100%',
                maxWidth: '300px',
                backgroundColor: 'button.secondary.background',
                color: 'button.primary.color',
                height: '40px',
                mt: '10px',
              }}
              onClick={login}
              disabled={isAuthenticating || !confirmationCode}
              loading={isAuthenticating}
              loadingIndicator={<ButtonLoadingIndicator />}
              variant="contained"
            >
              <Box>{t('anonymousLogin:confirmation')}</Box>
            </Button>
          </Grid>
        </Box>
      )}
    </>
  );
}
